// Cross-tab account transitions on BroadcastChannel("cb-account"), the
// channel and message shapes of offline contract C3: step 1
// {type:"owner_fence", owner} and step 10 {type:"owner_changed", from, to},
// owner keys "acct:<uuid>" or "guest". The fence state itself lives in
// src/lib/accountFence.ts (apiFetch reads it; it must stay prerender-safe).
//
// The order, for sign-out, account switch and account deletion alike:
// 1. beginOwnerTransition(from): peers still showing `from` are FENCED at once
//    (every API and Supabase request they start is held: apiFetch and
//    fencedFetch), and a short-lived marker in localStorage makes a tab that
//    boots meanwhile wait (main.tsx, and the same two fetch paths) instead of
//    reading the departing session out of shared storage;
// 2. the local session is removed (or replaced) and caches are purged;
// 3. endOwnerTransition(from, to): only now, with storage already holding the
//    next state, peers still showing `from` reload.
// cancelOwnerTransition(from) lifts the fence when the transition did not
// happen (a failed local sign-out). A fenced peer that hears nothing more
// reloads by itself after FENCE_MAX_MS. The full C3 fence (writers, queues,
// tombstones) lands with W3 and reuses this channel.
//
// No browser global at module scope (tests and the prerender import useAuth).
import { bumpSessionGeneration, captureRefreshRequest, isRefreshRequest, isTokenRequest, observeRefreshResponse } from './offline/refreshObserver'
import { readFenceMirror } from './offline/offlineStore'
import {
  hasForeignAccountTransition,
  isAccountFenced,
  liftFence,
  setFenced,
  setInitiating,
  waitForAccountTransition,
  whenUnfenced,
  writeMarker,
  __resetAccountFenceForTests,
} from './accountFence'

export {
  TRANSITION_MARKER_KEY,
  hasAccountTransitionInProgress,
  isAccountFenced,
  waitForAccountTransition,
} from './accountFence'

export const ACCOUNT_CHANNEL = 'cb-account'
/** A fenced tab that never hears the end of the transition reloads anyway. */
export const FENCE_MAX_MS = 15_000

export function ownerKey(userId: string | null | undefined): string {
  // eslint-disable-next-line i18next/no-literal-string -- an owner key, not copy
  return userId ? `acct:${userId.toLowerCase()}` : 'guest'
}

let currentOwner: string | null = null
let sender: BroadcastChannel | null = null
let fenceTimer: ReturnType<typeof setTimeout> | null = null

/** The owner this tab is showing now (useAuth calls it on every applied change). */
export function setCurrentOwner(userId: string | null): void {
  currentOwner = ownerKey(userId)
}

// This document's id on the channel. BroadcastChannel delivers a message to
// every OTHER channel object of the same name, those of the sending document
// included, so each message carries it and a listener ignores its own echo
// (the initiating tab must never fence or reload itself).
const DOC_ID = Math.random().toString(36).slice(2, 12)

/** The message was posted by this document (an echo). */
export function isOwnMessage(data: unknown): boolean {
  return !!data && typeof data === 'object' && (data as { src?: unknown }).src === DOC_ID
}

function post(message: object): void {
  if (typeof BroadcastChannel === 'undefined') return
  try {
    sender ??= new BroadcastChannel(ACCOUNT_CHANNEL)
    sender.postMessage({ ...message, src: DOC_ID })
  } catch {
    // A closed or unsupported channel: peers still get Supabase's own storage
    // event, and a fenced one reloads after FENCE_MAX_MS.
  }
}

/** Step 1: fence peers still showing `fromUserId`, before the session changes. Never throws. */
export function beginOwnerTransition(fromUserId: string | null): void {
  const from = ownerKey(fromUserId)
  setInitiating(true)
  writeMarker(from)
  // eslint-disable-next-line i18next/no-literal-string -- a message type
  post({ type: 'owner_fence', owner: from })
}

/** Step 10: the session is already removed or replaced; peers on `from` reload. Never throws. */
export function endOwnerTransition(fromUserId: string | null, toUserId: string | null): void {
  setInitiating(false)
  writeMarker(null)
  // eslint-disable-next-line i18next/no-literal-string -- a message type
  post({ type: 'owner_changed', from: ownerKey(fromUserId), to: ownerKey(toUserId) })
}

/** The transition did not happen (the local sign-out failed): lift the fence. */
export function cancelOwnerTransition(fromUserId: string | null): void {
  setInitiating(false)
  writeMarker(null)
  // eslint-disable-next-line i18next/no-literal-string -- a message type
  post({ type: 'owner_fence_lifted', owner: ownerKey(fromUserId) })
}

/**
 * A C3 transition message (src/lib/offline/transition.ts), with the W1
 * marker kept in step: owner_fence writes the boot-wait marker (this tab is
 * the initiator, so its own requests are never held), owner_fence_cancelled
 * and owner_changed clear it. Never throws.
 */
export function broadcastTransition(message: { type: string; owner?: string; from?: string; to?: string; fence_epoch?: number }): void {
  // eslint-disable-next-line i18next/no-literal-string -- a message type
  if (message.type === 'owner_fence' && typeof message.owner === 'string') {
    setInitiating(true)
    writeMarker(message.owner)
  } else {
    setInitiating(false)
    writeMarker(null)
  }
  post(message)
}

function clearFenceTimer(): void {
  if (fenceTimer) clearTimeout(fenceTimer)
  fenceTimer = null
}

/**
 * fetch() for the Supabase clients (global.fetch): held while this tab is
 * fenced, or while another tab is mid-transition (a tab that booted then
 * never heard the fence). The tab running the transition is never held.
 */
export const fencedFetch: typeof fetch = (input, init) => {
  if (isAccountFenced()) return whenUnfenced().then(() => fencedFetch(input, init))
  if (hasForeignAccountTransition()) return waitForAccountTransition().then(() => fencedFetch(input, init))
  const url = typeof input === 'string' ? input : input instanceof URL ? input.href : (input as Request).url
  if (!isRefreshRequest(url)) {
    if (!isTokenRequest(url)) return fetch(input, init)
    // Any other token grant (a sign-in): its 2xx establishes a new session,
    // so refresh answers bound to the older one stop applying (r3 #6).
    return fetch(input, init).then((res) => { if (res.ok) bumpSessionGeneration(); return res })
  }
  // A session refresh: what GoTrue answered is reported (offline C2,
  // src/lib/offline/refreshObserver.ts), bound to the token and account it
  // was sent for (captured NOW, before it leaves); the caller gets the same
  // response.
  const binding = captureRefreshRequest(init?.body)
  return fetch(input, init).then(
    (res) => { observeRefreshResponse(res, binding); return res },
    (err: unknown) => { observeRefreshResponse(null, binding); throw err },
  )
}

/**
 * Listen for peers' transitions. A tab still showing the departing owner is
 * fenced on owner_fence and reloads on owner_changed (or after FENCE_MAX_MS
 * with nothing more). A tab that already caught up (showing `to`, or never
 * showing `from`) does nothing, which also keeps the sender from reacting to
 * an echo from a peer.
 */
export function listenForOwnerChanges(onStale?: () => void): () => void {
  if (typeof BroadcastChannel === 'undefined') return () => {}
  const stale = onStale ?? (() => window.location.reload())
  const channel = new BroadcastChannel(ACCOUNT_CHANNEL)
  channel.onmessage = (event: MessageEvent) => {
    const data = event.data as { type?: unknown; owner?: unknown; from?: unknown; to?: unknown } | null
    if (!data || currentOwner === null || isOwnMessage(data)) return
    if (data.type === 'owner_fence' && data.owner === currentOwner) {
      setFenced()
      clearFenceTimer()
      fenceTimer = setTimeout(() => { fenceTimer = null; stale() }, FENCE_MAX_MS)
    } else if ((data.type === 'owner_fence_lifted' || data.type === 'owner_fence_cancelled') && data.owner === currentOwner) {
      // Only what the C3 mirror says NOW lifts the hold (Codex r2, note 11):
      // a stale cancel must not release requests while a newer transition
      // runs. Not lifted: the fence stays, and its FENCE_MAX_MS reload too.
      if (!cancelIsCurrent(data as { type: string; owner: string; fence_epoch?: unknown })) return
      clearFenceTimer()
      liftFence()
    } else if (data.type === 'owner_changed' && typeof data.from === 'string' && data.from !== data.to && data.from === currentOwner) {
      clearFenceTimer()
      stale()
    }
  }
  return () => channel.close()
}

/**
 * A peer's cancel / lift agrees with the authoritative C3 mirror: no
 * transition in progress, the same owner, and (a C3 cancel) exactly its
 * epoch. Without a mirror (no offline runtime on this browser) the legacy
 * lift stands; a C3 cancel needs the mirror.
 */
function cancelIsCurrent(m: { type: string; owner: string; fence_epoch?: unknown }): boolean {
  const mirror = readFenceMirror()
  if (!mirror) return m.type === 'owner_fence_lifted'
  if (mirror.transition_id !== null || mirror.owner_key !== m.owner) return false
  if (m.type === 'owner_fence_cancelled') return typeof m.fence_epoch === 'number' && m.fence_epoch === mirror.fence_epoch
  return true
}

/** Test seam. */
export function __resetAccountChannelForTests(): void {
  currentOwner = null
  sender?.close()
  sender = null
  clearFenceTimer()
  __resetAccountFenceForTests()
}
